import React, { useState, useCallback } from 'react';
import { Button } from '../../../shared/formElements/button';
import * as Yup from 'yup';
import {
  Form,
  FormContainer,
  FormSectionContainer,
  SectionContainer,
  ButtonGroupContainer,
  LoadingSpinner,
  ImageSplitFormSection,
  Line,
} from '../../../shared/Layout.styles';
import { Markdown } from '../../../shared/markdown';
import { balanceConstants } from '../../../utils/constants/balance';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import Input from '../../../shared/formElements/input';
import { BalanceTable } from './sections/BalanceTable';
import { Transaction, useContextDispatch, useContextState } from '../../context/context';
import axios from 'axios';
import { IconSectionCard } from '../../../shared/iconSectionCardSection';
import { globalConstants } from '../../../utils/constants/global';

interface FormData {
  eaid: string;
  last4: string;
}

interface TransactionsResponse {
  card_balance: number;
  card_status: string;
  plastic_expiry: string;
  page_number: number;
  page_size: number;
  total_items: number;
  total_pages: number;
  transactions: Transaction[];
}

const { eaid, last4 } = balanceConstants.validations;

export const Balance = () => {
  const dispatch = useContextDispatch();
  const { transactions, loading, balance, hasMore } = useContextState();

  const [serverErrors, setServerErrors] = useState("");
  const [showBalance, setShowBalance] = useState(false);
  const [isFormSubmitted, setFormSubmitted] = useState(false);
  const [currentCard, setCurrentCard] = useState<{ last4: string;
    eaid: string;
  } | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [paginationInfo, setPaginationInfo] = useState<{
    totalPages: number;
    totalItems: number;
    pageSize: number;
  } | null>(null);

  const { heading, subHeading, balanceText, balanceButtonText, defaultValue, defaultPageSize, last4text } = balanceConstants;
  const { expiredCardMessage } = globalConstants;


  const methods = useForm<FormData>({
    mode: "onBlur",
    reValidateMode: "onChange",
    resolver: yupResolver(validationSchema),
    shouldFocusError: false,
    defaultValues: {
      eaid: "",
      last4: "",
    },
  });

  const fetchTransactions = async (last4: string, eaid: string, pageNumber: number, pageSize: number): Promise<TransactionsResponse> => {
    const data = {
      last4,
      eaid,
    };
    const response = await axios.post(`/CardSummary`, data, {
      params: {
        page_size: pageSize,
        page_number: pageNumber,
      }
    });
    return response.data;
  };

  const onSubmit = async (formData: FormData) => {

    if (isFormSubmitted) return;

    setFormSubmitted(true);

    try {
      setServerErrors("");
      dispatch('START_LOADING');


      const transactionResponse = await fetchTransactions(formData.last4, formData.eaid, currentPage, defaultPageSize);
      dispatch('SET_BALANCE_TOTAL', transactionResponse.card_balance);
      setPaginationInfo({
        totalPages: transactionResponse.total_pages,
        totalItems: transactionResponse.total_items,
        pageSize: transactionResponse.page_size
      });

      dispatch('SET_BALANCES', transactionResponse.transactions);
      setCurrentCard({eaid: formData.eaid, last4: formData.last4 });

      if (transactionResponse.page_number < transactionResponse.total_pages) {
        dispatch("HAS_MORE");
      } else {
        dispatch("NO_MORE");
      }

      setShowBalance(true);

    } catch (err: any) {
      console.error("Error fetching balance/transactions:", err);

      if (err.response?.status === 404) {
        setServerErrors("Card Not Found");
      } else if (err.response?.status === 500) {
        setServerErrors(err.response.data.message || "Internal Server Error");
      } else if (err.response?.status === 700) {
        setServerErrors(expiredCardMessage);
      } else {
        setServerErrors('Something went wrong, please try again later or contact support if the problem persists');
      }

      dispatch("STOP_LOADING");
    } finally {
      setFormSubmitted(false);
      dispatch('STOP_LOADING');
    }
  };

  const getMore = useCallback(async () => {
    if (!currentCard || loading || !paginationInfo) return;

    const hasMorePages = currentPage < paginationInfo.totalPages;
    if (!hasMorePages) return;

    const nextPage = currentPage + 1;

    if (isFormSubmitted) return;

    setFormSubmitted(true);

    try {
      setServerErrors("");
      dispatch('START_LOADING');

      const transactionResponse = await fetchTransactions(currentCard.last4,
        currentCard.eaid,
        nextPage,
        paginationInfo.pageSize
      );

      setPaginationInfo({
        totalPages: transactionResponse.total_pages,
        totalItems: transactionResponse.total_items,
        pageSize: transactionResponse.page_size
      });

      setCurrentPage(transactionResponse.page_number);

      dispatch('ADD_BALANCES', transactionResponse.transactions);

      if (transactionResponse.page_number < transactionResponse.total_pages) {
        dispatch("HAS_MORE");
      } else {
        dispatch("NO_MORE");
      }

    } catch (err: any) {
      console.error("Error fetching more transactions:", err);

      if (err.response?.status === 404) {
        setServerErrors("Card Not Found");
      } else if (err.response?.status === 500) {
        setServerErrors(err.response.data.message || "Internal Server Error");
      } else if (err.response?.status === 700) {
        setServerErrors(expiredCardMessage);
      } else {
        setServerErrors('Error loading more transactions');
      }
    } finally {
      setFormSubmitted(false);
      dispatch('STOP_LOADING');
    }
  }, [
    currentCard,
    loading,
    paginationInfo,
    currentPage,
    isFormSubmitted,
    expiredCardMessage,
    dispatch,
    fetchTransactions
  ]);

  const { control, formState: { errors } } = methods;

  return (
    <>
      <Line />
      <FormProvider {...methods}>
        <Form onSubmit={methods.handleSubmit(onSubmit)}>
          <SectionContainer>
            <Markdown children={heading} />
            {!showBalance && (
              <Markdown children={subHeading} className="balanceText" />
            )}
            <FormContainer>
              {showBalance ? (
                <BalanceTable
                  transactions={transactions}
                  balance={balance}
                  hasMore={hasMore}
                  getMore={getMore}
                  loading={loading}
                  currentPage={currentPage}
                  totalPages={paginationInfo?.totalPages}
                  totalTransactions={paginationInfo?.totalItems}
                  pageSize={paginationInfo?.pageSize || 200}
                  serverErrors={serverErrors}
                />
              ) : (
                <>
                  <FormSectionContainer className="balanceSection">
                    <Controller
                      render={(props) => (
                        <Input
                          {...props}
                          type="text"
                          label="Unique Card ID*"
                          fieldName="eaid"
                          validation={
                            errors?.eaid?.message
                          }
                          tooltip="How to find my Unique Card ID?"
                          tooltipLink="https://vaultps.zendesk.com/hc/en-au/articles/4416855429263-Where-do-I-find-my-card-EAID-"
                          width="100%"
                        />
                      )}
                      name="eaid"
                      control={control}
                      defaultValue={defaultValue.eaid}
                    />
                    <Markdown children={balanceText} className="subText" />
                    <Controller
                      render={(props) => (
                        <Input
                          {...props}
                          type="text"
                          label="Last 4 Digits of Card Number*"
                          fieldName="last4"
                          validation={
                            errors?.last4?.message || serverErrors
                          }
                          tooltip="eg if your card number is '1234 **** **** 5678', enter 5678"
                          width="100%"
                        />
                      )}
                      name="last4"
                      control={control}
                    />
                    <Markdown children={last4text} className="subText" />
                    <ButtonGroupContainer>
                      <Button
                        type="submit"
                        disabled={loading || isFormSubmitted}
                      >
                        {loading ? <LoadingSpinner show /> : balanceButtonText}
                      </Button>
                    </ButtonGroupContainer>
                  </FormSectionContainer>
                  <FormSectionContainer>
                    {process.env.REACT_APP_CARD_OR_ICONS === "CARD" ? (
                      <ImageSplitFormSection
                        src={process.env.REACT_APP_CARD_FRONT ?? ""}
                      />
                    ) : (
                      <IconSectionCard />
                    )}
                  </FormSectionContainer>
                </>
              )}
            </FormContainer>
          </SectionContainer>
        </Form>
      </FormProvider>
    </>
  );
};

const validationSchema = Yup.object().shape({
  eaid: Yup.string()
    .required(eaid.required)
    .matches(/^[a-zA-Z0-9]*$/, {
      message:eaid.format,
      excludeEmptyString: true,
    }),
  last4: Yup.string()
    .required(last4.required)
    .matches(/^[0-9]{4}$/, {
      message:last4.format,
      excludeEmptyString: true,
    }),
});